import { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  computeVenueCost,
  groupReviewsByVenue,
  inclusionsFromLegacy,
  sortVenues,
  summarizeReviews,
  VENUE_STATUSES,
  type Venue,
  type VenueSort,
  type VenueStatus,
  type Wedding,
} from '@guabi/shared';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Pill } from '@/components/ui/Pill';
import { Stat, StatStrip } from '@/components/ui/Stat';
import { SelectInput, TextInput } from '@/components/ui/Field';
import { longDate, money } from '@/lib/format';
import { useAuth } from '@/features/auth/AuthProvider';
import { AddVenue } from './AddVenue';
import { CompareVenues } from './CompareVenues';
import { VenueDrawer } from './VenueDrawer';
import { Stars } from './Stars';
import { useVenues } from './queries';
import { useWeddingSummary } from '@/features/shell/queries';

const SORTS: VenueSort[] = ['rating', 'total', 'quote', 'tour', 'name'];

/** A booked venue is settled; a rejected one is history. Both read differently. */
const toneFor = (status: VenueStatus) =>
  status === 'booked'
    ? 'booked'
    : status === 'rejected'
      ? 'declined'
      : status === 'shortlisted'
        ? 'attending'
        : 'neutral';

export function VenuesTab() {
  const { t } = useTranslation();
  const { wedding, canWrite } = useOutletContext<{ wedding: Wedding; canWrite: boolean }>();
  const wedId = wedding.wedId;
  const currency = wedding.settings.currency;
  const auth = useAuth();

  const venues = useVenues(wedId);
  const [sort, setSort] = useState<VenueSort>('rating');
  const [statusFilter, setStatusFilter] = useState<VenueStatus | 'all'>('all');
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<Venue | undefined>();
  const [adding, setAdding] = useState(false);
  const [comparing, setComparing] = useState(false);

  // The guest list already computes what the room has to hold — attending plus
  // maybe plus no reply yet — and the summary endpoint already returns it, so
  // capacity fit costs no new server work.
  const summary = useWeddingSummary(wedId);
  const headcount = summary.data?.guests.anticipatedHeadcount ?? 0;

  const all = venues.data?.items ?? [];
  const reviews = venues.data?.reviews ?? [];
  const stats = venues.data?.stats;

  const byVenue = useMemo(() => groupReviewsByVenue(reviews), [reviews]);

  const cards = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = all
      .filter((v) => statusFilter === 'all' || v.status === statusFilter)
      .filter(
        (v) =>
          !q ||
          v.name.toLowerCase().includes(q) ||
          (v.address?.city ?? '').toLowerCase().includes(q),
      );
    return sortVenues(filtered, sort, byVenue);
  }, [all, byVenue, query, sort, statusFilter]);

  // Kept fresh from the list, so the drawer reflects a save without reopening.
  const openVenue = editing ? all.find((v) => v.venueId === editing.venueId) : undefined;

  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="eyebrow">{t('nav.venues')}</p>
          <h1 className="font-display text-display">{t('venues.heading')}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canWrite && <Button onClick={() => setAdding(true)}>{t('venues.add')}</Button>}
          {/* Comparing is reading, so it stays for everyone. Two is the fewest
              that can be compared. */}
          {all.length > 1 && (
            <Button variant="secondary" onClick={() => setComparing((c) => !c)}>
              {comparing ? t('venues.backToList') : t('venues.compare')}
            </Button>
          )}
        </div>
      </div>

      <StatStrip>
        <Stat label={t('venues.total')} value={stats?.total ?? 0} />
        <Stat
          label={t('venues.statusValue.shortlisted')}
          value={stats?.shortlisted ?? 0}
          sub={`${String(stats?.byStatus.toured ?? 0)} ${t('venues.toured')}`}
        />
        <Stat
          label={t('venues.averageQuote')}
          value={
            stats?.averageQuoteCents === null || stats?.averageQuoteCents === undefined
              ? '—'
              : money(stats.averageQuoteCents, currency)
          }
          sub={t('venues.averageQuoteSub')}
        />
        <Stat
          label={t('venues.topRated')}
          value={
            stats?.topRatedVenueId
              ? (all.find((v) => v.venueId === stats.topRatedVenueId)?.name ?? '—')
              : '—'
          }
        />
        <Stat label={t('venues.statusValue.booked')} value={stats?.booked ?? 0} />
      </StatStrip>

      {!comparing && (
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex-1">
            <span className="sr-only">{t('venues.search')}</span>
            <TextInput
              type="search"
              placeholder={t('venues.search')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
          <label className="flex items-center gap-2 text-caption text-leaf-soft">
            {t('venues.sortBy')}
            <SelectInput value={sort} onChange={(e) => setSort(e.target.value as VenueSort)}>
              {SORTS.map((s) => (
                <option key={s} value={s}>
                  {t(`venues.sortValue.${s}`)}
                </option>
              ))}
            </SelectInput>
          </label>
          <label className="flex items-center gap-2 text-caption text-leaf-soft">
            {t('venues.status')}
            <SelectInput
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as VenueStatus | 'all')}
            >
              <option value="all">{t('venues.allStatuses')}</option>
              {VENUE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`venues.statusValue.${s}`)}
                </option>
              ))}
            </SelectInput>
          </label>
        </div>
      )}

      {comparing && all.length > 0 && (
        <CompareVenues wedId={wedId} venues={all} reviewsByVenue={byVenue} headcount={headcount} />
      )}

      {venues.isLoading && <p className="text-body text-leaf-soft">{t('common.loading')}</p>}
      {venues.isError && <p className="text-body text-leaf-soft">{t('common.somethingWrong')}</p>}

      {!comparing && venues.data && cards.length === 0 && (
        <EmptyState
          title={all.length === 0 ? t('empty.venuesTitle') : t('venues.noMatchTitle')}
          body={all.length === 0 ? t('empty.venuesBody') : t('venues.noMatchBody')}
          action={
            all.length === 0 && canWrite ? (
              <Button onClick={() => setAdding(true)}>{t('venues.add')}</Button>
            ) : undefined
          }
        />
      )}

      {!comparing && cards.length > 0 && (
        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {cards.map((v) => {
            const summary = summarizeReviews(byVenue[v.venueId] ?? []);
            const cost = computeVenueCost(v);
            const included = inclusionsFromLegacy(v).filter((i) => i.status === 'included');
            return (
              <li key={v.venueId} className="panel flex flex-col gap-3 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-col">
                    <span className="font-display text-[22px] leading-tight">{v.name}</span>
                    {v.address?.city && (
                      <span className="text-caption text-leaf-soft">{v.address.city}</span>
                    )}
                  </div>
                  <Pill tone={toneFor(v.status ?? 'found')}>
                    {t(`venues.statusValue.${v.status ?? 'found'}`)}
                  </Pill>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {summary.average === null ? (
                    <span className="text-caption text-leaf-soft">{t('venues.noReviews')}</span>
                  ) : (
                    <>
                      <Stars value={Math.round(summary.average)} />
                      <span className="tabular text-caption text-leaf-soft">
                        {summary.average} · {t('venues.reviewCount', { count: summary.count })}
                      </span>
                      {/* The number the plain average hides. */}
                      {summary.agreementGap !== null && summary.agreementGap > 1 && (
                        <Pill tone="maybe">{t('venues.split')}</Pill>
                      )}
                    </>
                  )}
                </div>

                {summary.dealbreakers.length > 0 && (
                  <span className="text-caption text-status-declinedFg">
                    {t('venues.hasDealbreaker')}
                  </span>
                )}

                <dl className="flex flex-wrap gap-x-6 gap-y-1 text-caption">
                  {/* The real total leads, because the quote is the number that
                      misleads: a venue covering nothing is dearer than its price. */}
                  <div className="flex gap-1">
                    <dt className="text-leaf-soft">{t('venues.realTotal')}</dt>
                    <dd className="tabular">
                      {cost.realTotalCents === undefined
                        ? '—'
                        : cost.isPartial
                          ? t('venues.atLeast', {
                              amount: money(cost.realTotalCents, v.currency ?? currency),
                            })
                          : money(cost.realTotalCents, v.currency ?? currency)}
                    </dd>
                  </div>
                  {cost.extrasCents > 0 && (
                    <div className="flex gap-1">
                      <dt className="text-leaf-soft">{t('venues.quote')}</dt>
                      <dd className="tabular text-leaf-soft">
                        {money(cost.quoteCents ?? 0, v.currency ?? currency)}
                      </dd>
                    </div>
                  )}
                  <div className="flex gap-1">
                    <dt className="text-leaf-soft">{t('venues.capacity')}</dt>
                    <dd className="tabular">{v.capacity ?? '—'}</dd>
                  </div>
                  <div className="flex gap-1">
                    <dt className="text-leaf-soft">{t('venues.tourDate')}</dt>
                    <dd>{v.tourDate ? longDate(v.tourDate) : '—'}</dd>
                  </div>
                </dl>

                {/* Only what the venue covers — the pills are the good news, and
                    the extras are already priced into the total above. Read
                    through the legacy fallback so a venue created before this
                    feature still shows its list. */}
                {included.length > 0 && (
                  <span className="flex flex-wrap gap-1">
                    {included.slice(0, 4).map((i) => (
                      <Pill key={i.key} tone="attending">
                        {i.label ?? t(`inclusions.${i.key}`)}
                      </Pill>
                    ))}
                    {included.length > 4 && <Pill>{`+${String(included.length - 4)}`}</Pill>}
                  </span>
                )}

                <div className="mt-auto flex items-center gap-3 pt-1">
                  <button
                    type="button"
                    onClick={() => setEditing(v)}
                    className="min-h-tap text-caption text-brass-text underline underline-offset-4"
                  >
                    {byVenue[v.venueId]?.some((r) => r.userId === auth.userId)
                      ? t('venues.openAndReview')
                      : t('venues.openAndRate')}
                  </button>
                  {v.website && (
                    <a
                      href={v.website}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="min-h-tap text-caption text-leaf-soft underline underline-offset-4"
                    >
                      {t('venues.website')}
                    </a>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <AddVenue
        wedId={wedId}
        open={adding}
        onClose={() => setAdding(false)}
        onAdded={(v) => setEditing(v)}
      />

      {openVenue && (
        <VenueDrawer
          canWrite={canWrite}
          // Keyed on the id alone, deliberately. Including `updatedAt` remounted
          // the drawer on every save — and on any refetch caused by the other
          // partner saving — which threw away whatever was being typed at the
          // time. Switching venue still remounts, which is the only case that
          // should reset the form.
          key={openVenue.venueId}
          wedId={wedId}
          venue={openVenue}
          reviews={byVenue[openVenue.venueId] ?? []}
          currency={currency}
          myUserId={auth.userId}
          open
          onClose={() => setEditing(undefined)}
        />
      )}
    </section>
  );
}
